import React, { memo, useMemo } from 'react';
import { useInterpret } from '@xstate/react';
import type { ActorRefFrom } from 'xstate';
import { networkMonitorMachine } from '@sweetgreen/garnish';

import type { dietaryRestrictionsMachine } from '../../components/DietaryRestrictions/dietaryRestrictionsMachine/dietaryRestrictionsMachine';
import { useDietaryRestrictionsInterpret } from '../../components/DietaryRestrictions/dietaryRestrictionsMachine/useDietaryRestrictionsInterpret';
import { overlayMachine } from '../../components/Overlays/Overlays.machine';
import { cartMachine } from '../../hooks/useCart/Cart.machine/Cart.machine';
import { reorderMachine } from '../../screens/ReorderingScreen/machines/Reorder.machine';
import type { AuthMachineRefType } from '../AuthMachine/useInitAuthMachine';
import { useInitAuthMachine } from '../AuthMachine/useInitAuthMachine';
import type { LastInteractedStoreMachineRefType } from '../LastInteractedStore/machine';
import { useInitLastInteractedStoreMachine } from '../LastInteractedStore/machine';

//
// ─── CONTEXT SETUP ──────────────────────────────────────────────────────────────
//

const INITIAL_CONTEXT_VALUE = undefined;

const GlobalAppStateContext = React.createContext<ContextValue>(
  INITIAL_CONTEXT_VALUE,
);

// TYPES
type ContextValue = typeof INITIAL_CONTEXT_VALUE | GlobalAppStateContextType;

type GlobalAppStateContextType = {
  readonly authMachineRef: AuthMachineRefType;
  readonly lastInteractedStoreMachineRef: LastInteractedStoreMachineRefType;
  readonly cartMachineRef: ActorRefFrom<typeof cartMachine>;
  readonly networkMonitorMachineRef: ActorRefFrom<typeof networkMonitorMachine>;
  readonly overlayMachineRef: ActorRefFrom<typeof overlayMachine>;
  readonly reorderMachineRef: ActorRefFrom<typeof reorderMachine>;
  readonly dietaryRestrictionsMachineRef: ActorRefFrom<
    typeof dietaryRestrictionsMachine
  >;
};

//
// ─── PROVIDER ────────────────────────────────────────────────────────────────
//

const useInterpretedMachineRefs = () => {
  return {
    authMachineRef: useInitAuthMachine(),
    lastInteractedStoreMachineRef: useInitLastInteractedStoreMachine(),
    dietaryRestrictionsMachineRef: useDietaryRestrictionsInterpret(),
    cartMachineRef: useInterpret(cartMachine),
    networkMonitorMachineRef: useInterpret(networkMonitorMachine),
    overlayMachineRef: useInterpret(overlayMachine),
    reorderMachineRef: useInterpret(reorderMachine),
  };
};

export const GlobalAppState = memo((props: GlobalAppStateProps) => {
  const machineRefs = useInterpretedMachineRefs();
  const contextValue = useMemo(() => machineRefs, [machineRefs]);

  return (
    <GlobalAppStateContext.Provider value={contextValue}>
      {props.children}
    </GlobalAppStateContext.Provider>
  );
});

type GlobalAppStateProps = {
  readonly children: React.ReactNode;
};

//
// ─── HOOK ────────────────────────────────────────────────────────────────────
//

/**
 * CAUTION: Prefer more targeted hooks.
 * @see {@link: https://twitter.com/kentcdodds/status/1121440768277663745?lang=en}
 * >I never expose my context object to other modules, instead I create a custom
 *  Provider component and a custom hook consumer and expose those.
 *
 * NOTE: This is only intended to be used by other useContext-derived hooks eg useBagMachine
 */
export const useGlobalAppState = () => {
  const context = React.useContext(GlobalAppStateContext);

  if (!context) {
    throw new Error(`cannot be rendered outside the <GlobalAppState />`);
  }

  return context;
};
